import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, clearToken, getToken, setActingOrg, clearActingOrg } from "@/lib/api";

const AuthContext = createContext(null);
const USER_KEY = "jam_user";
const cachedUser = () => { try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; } };
const cacheUser = (u) => { try { localStorage.setItem(USER_KEY, JSON.stringify(u)); } catch { /* ignore */ } };
const clearUser = () => localStorage.removeItem(USER_KEY);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, obj = logged in
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    // Restore the session instantly from cache so a returning phone user
    // never sees the login screen while /auth/me is still in flight.
    const cu = cachedUser();
    if (cu) { setUser(cu); setLoading(false); }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      cacheUser(data);
    } catch (e) {
      // ONLY log out on a real auth rejection. A transient network error
      // (e.g. switching back from WhatsApp) must NOT drop the session.
      if (e.response && (e.response.status === 401 || e.response.status === 403)) {
        clearToken(); clearUser(); setUser(false);
      } else if (!cu) {
        setUser(false); // no cache to fall back on
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    cacheUser(data.user);
    setUser(data.user);
    try { sessionStorage.setItem("jam_just_logged_in", "1"); } catch { /* ignore */ }
    return data.user;
  };

  const logout = () => {
    clearToken(); clearUser(); clearActingOrg();
    setUser(false);
  };

  // Owner only: switch the whole session into another organisation (or back home).
  // A full reload guarantees every panel refetches with the new acting-org header.
  const switchOrg = (orgId) => {
    setActingOrg(orgId || null);
    window.location.reload();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, switchOrg }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
